import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { stats } from '../data/mock';
import { useAuth } from '../context/AuthContext';

const AnimatedNumber = ({ target, prefix = '', suffix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const numericTarget = typeof target === 'number' ? target : parseFloat(target);
    const duration = 2000;
    const steps = 60;
    const stepValue = numericTarget / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += stepValue;
      if (current >= numericTarget) {
        setCount(numericTarget);
        clearInterval(timer);
      } else {
        setCount(decimals > 0 ? current : Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [target, decimals]);

  const displayValue = decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString();

  return (
    <span>
      {prefix}{displayValue}{suffix}
    </span>
  );
};

const HeroSection = () => {
  const { user } = useAuth();

  return (
    <div className="flex px-8 pb-20 pt-12 relative z-10">
      {/* Left Content */}
      <div className="flex-1 max-w-xl">
        {/* Main Headline */}
        <h1 className="text-5xl font-light text-white mb-5 leading-[1.15]">
          Shape the future<br />
          <span className="text-white font-normal">of AI</span> with your expertise.
        </h1>

        {/* Subheadline */}
        <p className="text-white/60 text-lg mb-10">
          Find top-tier, remote AI roles. Get matched with opportunities that value your skills.
        </p>

        {/* CTA Buttons */}
        <div className="flex gap-4">
          {user ? (
            user.onboarding_completed ? (
              <Link to="/dashboard">
                <Button className="bg-white text-indigo-600 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-medium">
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            ) : (
              <Link to="/onboarding">
                <Button className="bg-white text-indigo-600 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-medium">
                  Complete Profile
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            )
          ) : (
            <>
              <Link to="/register">
                <Button className="bg-white text-indigo-600 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-medium">
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/roles">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg">
                  Browse Roles
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Right Stats Sidebar */}
      <div className="hidden lg:flex flex-col gap-10 ml-auto text-right pr-8">
        <div>
          <p className="text-white/50 text-sm leading-tight">Average<br />hourly rate</p>
          <p className="text-amber-300 text-5xl font-semibold mt-2">
            $<AnimatedNumber target={stats.averageHourlyRate} />
          </p>
        </div>

        <div>
          <p className="text-white/50 text-sm leading-tight">Roles<br />created</p>
          <p className="text-amber-300 text-5xl font-semibold mt-2">
            <AnimatedNumber target={55} suffix="k" />
          </p>
        </div>

        <div>
          <p className="text-white/50 text-sm leading-tight">Daily<br />payouts</p>
          <p className="text-amber-300 text-5xl font-semibold mt-2">
            $<AnimatedNumber target={2.0} suffix="M" decimals={1} />
          </p>
        </div>

        <div>
          <p className="text-white/50 text-sm leading-tight">People hired<br />last week</p>
          <p className="text-amber-300 text-5xl font-semibold mt-2">
            <AnimatedNumber target={stats.peopleHiredLastWeek} />
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
